import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { logoutSession } from 'config/axiosInstance';
import { clearSession } from 'config/session';
import { formatBusinessDateTime } from 'config/time';
import FetchFailed from 'reusable/FetchFailed';
import Pagination from 'reusable/pagination';
import Search from 'reusable/search';
import TableScroll from 'reusable/TableScroll';
import { useLatestRequest } from 'reusable/useLatestRequest';
import { LOGIN_PATH } from 'router/pathName';
import {
  currentUserId,
  inviteOperator,
  listOperators,
  OPERATOR_PAGE_SIZE,
  OperatorAccount,
  removeOperator,
} from './operatorApi';
import {
  ConfirmDialog,
  InviteDialog,
  isUnresolved,
  StatusBadge,
  SuccessNotice,
  primaryButton,
  pageAfterRemoval,
  pageToRecover,
  useOperatorErrors,
  useOutcomeMessages,
  useRoleLabel,
} from './OperatorUi';

const TITLE_ID = 'operator-operators-title';

/**
 * YAC-2026-009 PR5b。運営の一覧（運営）。状態（status）と、有効な運営として数えるか（counted）を出す。
 * 招待と、運営を外す操作（確認のダイアログの後）。最後の有効な運営は外せない（409 last_operator）。
 * 自分自身を外すときは、確認のダイアログで強く知らせ、外せたらログアウトする（運営の権限が無くなるため）。
 */
const OperatorList: React.FC = () => {
  const { t } = useTranslation('layout');
  const navigate = useNavigate();
  const errorsOf = useOperatorErrors();
  const outcomeMessages = useOutcomeMessages();
  const roleLabel = useRoleLabel();
  const me = currentUserId();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<OperatorAccount[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [removing, setRemoving] = useState<OperatorAccount | null>(null);
  const [busy, setBusy] = useState(false);
  const [removeErrors, setRemoveErrors] = useState<string[]>([]);
  const beginRequest = useLatestRequest();

  const load = useCallback(async () => {
    const isCurrent = beginRequest();
    setLoading(true);
    try {
      const result = await listOperators(page, search);
      if (!isCurrent()) return;
      const recover = pageToRecover(page, OPERATOR_PAGE_SIZE, { count: result.count, rows: result.results.length });
      if (recover !== null) {
        setPage(recover);
        return;
      }
      setRows(result.results);
      setCount(result.count);
      setLoadError(null);
    } catch (failure) {
      if (!isCurrent()) return;
      // ページの外（404）なら、エラーにせず1ページ目を読む。
      const recover = pageToRecover(page, OPERATOR_PAGE_SIZE, { error: failure });
      if (recover !== null) {
        setPage(recover);
        return;
      }
      console.error('Failed to fetch operators', failure);
      setLoadError(failure);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [beginRequest, page, search]);

  useEffect(() => {
    load();
  }, [load]);

  const invite = async (invitation: { email: string; name: string }) => {
    const outcome = await inviteOperator(invitation);
    const failed = outcomeMessages(outcome);
    if (failed) {
      if (outcome.status === 'unknown' || outcome.status === 'expired') load();
      return { messages: failed, unresolved: isUnresolved(outcome) };
    }
    const created = outcome.status === 'created' ? outcome.response?.data : undefined;
    setInviting(false);
    setNotice(t('operator.operators.invited', { email: created?.email ?? invitation.email }));
    load();
    return null;
  };

  const self = removing !== null && removing.id === me;

  const remove = async () => {
    if (!removing || busy) return;
    setBusy(true);
    setRemoveErrors([]);
    try {
      const result = await removeOperator(removing.id);
      if (self) {
        // 自分の運営の権限は無くなった。セッションを終えてログインへ戻す。
        await logoutSession();
        clearSession();
        navigate(LOGIN_PATH, { replace: true });
        return;
      }
      setRemoving(null);
      setNotice(
        t(result === 'deleted' ? 'operator.operators.removed-deleted' : 'operator.operators.removed-role', {
          name: removing.name,
        })
      );
      // 外したら一覧から消える。今のページの最後の1件なら、1つ前のページを読む（#387 の Codex の指摘 4368575053）。
      const next = pageAfterRemoval(page, rows.length);
      if (next !== page) setPage(next);
      else load();
    } catch (failure) {
      console.error('Failed to remove operator', failure);
      setRemoveErrors(errorsOf(failure));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="flex flex-col gap-4 px-4 py-3">
      <div className="flex items-center justify-between gap-4">
        <h1 id={TITLE_ID} className="text-title font-bold text-neutral/300">
          {t('operator.operators.title')}
        </h1>
        <button type="button" className={primaryButton} onClick={() => setInviting(true)}>
          {t('operator.operators.invite')}
        </button>
      </div>
      <SuccessNotice message={notice} onClose={() => setNotice(null)} />
      <div className="flex flex-col gap-4 rounded-md bg-white p-4">
        <p className="text-body-2 text-neutral/300">{t('operator.operators.counted-hint')}</p>
        <div className="w-80">
          <Search
            placeholder={t('operator.account.search')}
            onSearchChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
          />
        </div>
        {loading ? (
          <div role="status" className="py-8 text-center text-neutral/300">
            {t('ui.loading', { ns: 'common' })}
          </div>
        ) : loadError ? (
          <FetchFailed error={loadError} onRetry={load} />
        ) : rows.length === 0 ? (
          <p className="py-8 text-center text-neutral/300">{t('operator.operators.empty')}</p>
        ) : (
          <TableScroll labelledBy={TITLE_ID}>
            <table className="w-full text-left text-body-2 text-neutral/300">
              <thead className="bg-primary/50">
                <tr>
                  <th scope="col" className="px-3 py-2 font-medium">{t('operator.account.col-name')}</th>
                  <th scope="col" className="px-3 py-2 font-medium">{t('operator.account.col-email')}</th>
                  <th scope="col" className="px-3 py-2 font-medium">{t('operator.account.col-status')}</th>
                  <th scope="col" className="px-3 py-2 font-medium">{t('operator.operators.col-counted')}</th>
                  <th scope="col" className="px-3 py-2 font-medium">{t('operator.account.col-roles')}</th>
                  <th scope="col" className="px-3 py-2 font-medium">{t('operator.account.col-expires')}</th>
                  <th scope="col" className="px-3 py-2 font-medium">{t('operator.account.col-actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((account) => {
                  const mine = account.id === me;
                  const invited = account.status === 'invited' || account.status === 'invitation_expired';
                  return (
                    <tr key={account.id} className="border-b">
                      <td className="px-3 py-3 font-medium">
                        {account.name || '-'}
                        {mine ? (
                          <span className="ml-2 rounded border border-primary/300 px-1.5 py-0.5 text-caption text-primary/300">
                            {t('operator.account.you')}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-3 py-3">{account.email}</td>
                      <td className="px-3 py-3">
                        <StatusBadge status={account.status} />
                      </td>
                      <td className="px-3 py-3">
                        {account.counted ? t('operator.operators.counted-yes') : t('operator.operators.counted-no')}
                      </td>
                      <td className="px-3 py-3">{roleLabel(account.roles) || '-'}</td>
                      <td className="px-3 py-3">
                        {invited ? formatBusinessDateTime(account.invitation_expires_at, '-') : '-'}
                      </td>
                      <td className="px-3 py-3">
                        <button
                          type="button"
                          className="rounded-md border border-red-700 px-2 py-1 text-caption text-red-700"
                          aria-label={t('operator.action-label', { action: t('operator.operators.remove'), name: account.name })}
                          onClick={() => {
                            setRemoveErrors([]);
                            setRemoving(account);
                          }}
                        >
                          {t('operator.operators.remove')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroll>
        )}
        <Pagination allRows={count} rowsPerPage={OPERATOR_PAGE_SIZE} currentPage={page} onPageChange={setPage} />
      </div>
      {inviting ? (
        <InviteDialog
          title={t('operator.operators.invite-title')}
          lead={t('operator.operators.invite-lead')}
          submitLabel={t('operator.account.send-invite')}
          onSubmit={invite}
          onClose={() => setInviting(false)}
        />
      ) : null}
      {removing ? (
        <ConfirmDialog
          title={self ? t('operator.operators.remove-self-title') : t('operator.operators.remove-title')}
          body={
            self ? (
              <>
                <p className="font-bold text-red-700">{t('operator.operators.remove-self-warning')}</p>
                <p>{t('operator.operators.remove-self-confirm')}</p>
              </>
            ) : (
              <p>{t('operator.operators.remove-confirm', { name: removing.name })}</p>
            )
          }
          confirmLabel={self ? t('operator.operators.remove-self-button') : t('operator.operators.remove')}
          danger
          busy={busy}
          errors={removeErrors}
          onConfirm={remove}
          onClose={() => setRemoving(null)}
        />
      ) : null}
    </section>
  );
};

export default OperatorList;
